import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, TextInput } from '@carbon/react';
import { vnDayKey, timeZoneLabel } from '@ruby-cms/shared';
import { api } from '../api/client';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { Money } from '../components/format';
import { BarChart } from '../components/BarChart';
import { FilterBar, ExportButton } from '../components/Filters';
import {
  GRAIN_LABEL, GrainSwitch, Hours, KpiTile, LineChart, Num, PARTIAL_NOTE, PERIOD_WORD, Panel, Rate, ddmm, grainFilter, periodCell, periodTick,
  scopeFields, useOptions, useReportFilters, type Grain,
} from '../components/Analytics';

// Screen 4 of docs/cms-golive/kpi-catalogue.md: players and lifecycle, acquisition by agent, source,
// landing page and device, and the marketing cost per source (P1-D17). Vòng đời and Nguồn khách repeat
// their figures per day, week or month of the range (switch Ngày / Tuần / Tháng, URL key `grain`).

type Kpis = { registrations: number; ftd: number; activeBettors: number; loggedIn: number; logins: number; ggr: number; ngr: number | null; arpu: number | null };
type Tiles = { day: Kpis; yesterday: Kpis; lastWeek: Kpis; mtd: Kpis; lastMtd: Kpis };
type LifePeriod = {
  day: string; partial: boolean; registrations: number; activated: number; activationRate: number | null; ftd: number; converted: number; ftdConversion: number | null;
  active: number; loggedIn: number; logins: number; depositors: number; deposits: number; depositAmount: number; depositsPerDepositor: number | null;
  repeatDepositors: number; secondDeposits: number; secondDepositRate: number | null; ggr: number; ggrNew: number; ggrReturning: number;
  ngr: number | null; bonus: number | null; commission: number | null; arpu: number | null; arppu: number | null;
  priorActive: number; churned: number; churnRate: number | null; reactivated: number; vip: number[];
};
type Life = {
  grain: Grain; periods: LifePeriod[];
  from: string; to: string; activationDays: number;
  funnel: { registrations: number; activated: number; ftd: number; activationRate: number | null; ftdConversion: number | null; timeToFtdHours: { median: number | null; players: number } };
  activity: {
    series: { day: string; registrations: number; dau: number; wau: number; mau: number; loggedIn: number; logins: number }[];
    avgDau: number; mau: number; stickiness: number | null; loggedInPlayers: number; logins: number; loginsPerPlayer: number | null;
  };
  revenue: {
    ggr: number; ggrNew: number; ggrReturning: number; ngr: number | null; bonus: number | null; commission: number | null;
    activeBettors: number; depositors: number; arpu: number | null; arppu: number | null; arpuNgr: number | null; arppuNgr: number | null;
  };
  churn: { asOf: string; priorActive: number; churned: number; rate: number | null; reactivated: number };
  vip: { order: string[]; counts: { name: string; players: number }[]; up: number; down: number; same: number; moves: { from: string | null; to: string | null; players: number }[] };
  tiles: Tiles;
};

const TILES: [keyof Kpis, string, boolean][] = [
  ['registrations', 'Đăng ký', false], ['ftd', 'Nạp lần đầu (FTD)', false], ['activeBettors', 'Người đặt cược', false],
  ['loggedIn', 'Người đăng nhập', false], ['ggr', 'Lãi nhà cái (GGR)', true], ['ngr', 'Lãi ròng (NGR)', true], ['arpu', 'GGR mỗi người cược', true],
];

/** The lifecycle figures of each day, week or month of the range: players, money, churn and VIP. */
function LifePeriods({ d }: { d: Life }) {
  const g = d.grain, word = PERIOD_WORD[g];
  const rows = [...d.periods].reverse();
  const head = <th>{GRAIN_LABEL[g]}</th>;
  const cell = (r: LifePeriod) => <td className="num is-title">{periodCell(r.day, g, r.partial)}</td>;
  const money = (v: number | null, signed = false) => (v === null ? <span className="muted">-</span> : <Money v={v} signed={signed} />);
  const partial = g !== 'day' ? ' ' + PARTIAL_NOTE : '';
  const ticks = d.periods.map((r) => periodTick(r.day, g, r.partial));
  return (
    <>
      <Panel title={`Người chơi theo ${word}`}>
        <div className="split split--even">
          <LineChart title={`Đăng ký và nạp lần đầu theo ${word}`} format={(v) => String(Math.round(v))} labels={ticks} height={180}
            series={[{ label: 'Đăng ký', points: d.periods.map((r) => r.registrations) }, { label: 'Nạp lần đầu', points: d.periods.map((r) => r.ftd) }]} />
          <LineChart title={`Người cược và người đăng nhập theo ${word}`} format={(v) => String(Math.round(v))} labels={ticks} height={180}
            series={[{ label: 'Người cược', points: d.periods.map((r) => r.active) }, { label: 'Người đăng nhập', points: d.periods.map((r) => r.loggedIn) }]} />
        </div>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">Đăng ký</th><th className="r">Kích hoạt</th><th className="r">Tỷ lệ kích hoạt</th><th className="r">Nạp lần đầu trong {word}</th>
              <th className="r">Người đăng ký đã nạp</th><th className="r">Tỷ lệ nạp</th><th className="r">Người cược</th><th className="r">Người đăng nhập</th><th className="r">Lượt đăng nhập</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Num v={r.registrations} /></td><td className="r"><Num v={r.activated} /></td><td className="r"><Rate v={r.activationRate} /></td>
                <td className="r"><Num v={r.ftd} /></td><td className="r"><Num v={r.converted} /></td><td className="r"><Rate v={r.ftdConversion} /></td>
                <td className="r"><Num v={r.active} /></td><td className="r"><Num v={r.loggedIn} /></td><td className="r"><Num v={r.logins} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">Kích hoạt = người đăng ký trong {word} có cược trong {d.activationDays} ngày đầu. Nạp lần đầu trong {word} = người nạp lần đầu tiên trong {word} đó, đăng ký lúc nào cũng được; người đăng ký đã nạp = người đăng ký trong {word} đó đã nạp lần đầu tính đến nay. Người cược và người đăng nhập đếm mỗi tài khoản một lần trong mỗi {word}.{partial}</p>
      </Panel>

      <Panel title={`Nạp tiền theo ${word}`}>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">Người nạp</th><th className="r">Lần nạp</th><th className="r">Tiền nạp</th><th className="r">Lần nạp mỗi người</th>
              <th className="r">Người nạp lại</th><th className="r">Nạp lần đầu</th><th className="r">Đã nạp lần hai</th><th className="r">Tỷ lệ nạp lần hai</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Num v={r.depositors} /></td><td className="r"><Num v={r.deposits} /></td><td className="r"><Money v={r.depositAmount} /></td>
                <td className="r"><Num v={r.depositsPerDepositor} digits={2} /></td><td className="r"><Num v={r.repeatDepositors} /></td>
                <td className="r"><Num v={r.ftd} /></td><td className="r"><Num v={r.secondDeposits} /></td><td className="r"><Rate v={r.secondDepositRate} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">Người nạp lại = người nạp trong {word} đã nạp lần đầu trước {word} đó. Đã nạp lần hai = người nạp lần đầu trong {word} đã nạp thêm ít nhất một lần tính đến nay; tỷ lệ tính trên số nạp lần đầu của {word}.{partial}</p>
      </Panel>

      <Panel title={`Doanh thu theo ${word}`}>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">GGR</th><th className="r">GGR người mới</th><th className="r">GGR người cũ</th><th className="r">Thưởng</th><th className="r">Hoa hồng</th>
              <th className="r">NGR</th><th className="r">ARPU</th><th className="r">ARPPU</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Money v={r.ggr} signed /></td><td className="r"><Money v={r.ggrNew} signed /></td><td className="r"><Money v={r.ggrReturning} signed /></td>
                <td className="r">{money(r.bonus)}</td><td className="r">{money(r.commission)}</td><td className="r">{money(r.ngr, true)}</td>
                <td className="r">{money(r.arpu, true)}</td><td className="r">{money(r.arppu, true)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">GGR người mới = của người đăng ký trong {word} đó, còn lại là người cũ. ARPU = GGR / người cược, ARPPU = GGR / người nạp của {word}. Thưởng, hoa hồng và NGR không tính khi lọc theo game.{partial}</p>
      </Panel>

      <Panel title={`Rời bỏ, quay lại và hạng VIP theo ${word}`}>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">Người nạp có cược 30 ngày trước</th><th className="r">Rời bỏ</th><th className="r">Tỷ lệ rời bỏ</th><th className="r">Quay lại</th>
              {d.vip.order.map((t) => <th key={t} className="r">VIP {t}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Num v={r.priorActive} /></td><td className="r"><Num v={r.churned} /></td><td className="r"><Rate v={r.churnRate} /></td><td className="r"><Num v={r.reactivated} /></td>
                {r.vip.map((n, i) => <td key={d.vip.order[i]} className="r"><Num v={n} /></td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">Tính tại ngày cuối mỗi {word} (ngày cuối của khoảng nếu {word} chưa hết), chỉ người đã nạp tiền: rời bỏ = có cược trong 30 ngày trước đó (ngày thứ 59 đến 30 trước ngày cuối) nhưng không cược trong 30 ngày gần nhất. Quay lại = cược lại trong {word} sau hơn 30 ngày không cược. Hạng VIP theo doanh số 30 ngày tính đến ngày cuối {word}; ngưỡng hạng trong Cài đặt.{partial}</p>
      </Panel>
    </>
  );
}

function Lifecycle() {
  const o = useOptions();
  const f = useReportFilters(30, ['grain']);
  const [grain, pickGrain] = grainFilter(f);
  const q = useQuery({ queryKey: ['analytics', 'players', f.query], queryFn: () => api.get<Life>('/api/admin/analytics/players', f.query) });
  const d = q.data;
  return (
    <>
      <GrainSwitch value={grain} onChange={pickGrain} />
      <FilterBar id="sp" fields={scopeFields(o.data, { game: true })} filters={f} exportPath="/api/admin/analytics/players" />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d ? null : (
        <>
          <p className="muted note">Ô số liệu là ngày {ddmm(d.to)}/{d.to.slice(0, 4)} (ngày cuối của khoảng), so với hôm trước, cùng thứ tuần trước, và từ đầu tháng so với cùng kỳ tháng trước.</p>
          <div className="kpis">
            {TILES.map(([k, l, money], i) => (
              <KpiTile key={k} label={l} money={money} lead={i === 0}
                now={d.tiles.day[k]} yesterday={d.tiles.yesterday[k]} lastWeek={d.tiles.lastWeek[k]} mtd={d.tiles.mtd[k]} lastMtd={d.tiles.lastMtd[k]} />
            ))}
          </div>

          <LifePeriods d={d} />

          <Panel title="Phễu đăng ký (cả khoảng)">
            <table className="plain">
              <thead><tr><th>Bước</th><th className="r">Người</th><th className="r">Tỷ lệ trên đăng ký</th></tr></thead>
              <tbody>
                <tr><td className="is-title">Đăng ký trong khoảng</td><td className="r"><Num v={d.funnel.registrations} /></td><td className="r">100%</td></tr>
                <tr><td className="is-title">Kích hoạt (cược trong {d.activationDays} ngày đầu)</td><td className="r"><Num v={d.funnel.activated} /></td><td className="r"><Rate v={d.funnel.activationRate} /></td></tr>
                <tr><td className="is-title">Nạp lần đầu (đến nay)</td><td className="r"><Num v={d.funnel.ftd} /></td><td className="r"><Rate v={d.funnel.ftdConversion} /></td></tr>
              </tbody>
            </table>
            <p className="muted">Thời gian từ đăng ký đến nạp lần đầu (trung vị): <Hours v={d.funnel.timeToFtdHours.median} />, trên {d.funnel.timeToFtdHours.players} người.</p>
          </Panel>

          <Panel title="Người chơi hoạt động (DAU / WAU / MAU)">
            <LineChart title="DAU WAU MAU theo ngày" labels={d.activity.series.map((r) => ddmm(r.day))} format={(v) => String(Math.round(v))}
              series={[
                { label: 'DAU (người cược trong ngày)', points: d.activity.series.map((r) => r.dau) },
                { label: 'WAU (7 ngày)', points: d.activity.series.map((r) => r.wau) },
                { label: 'MAU (30 ngày)', points: d.activity.series.map((r) => r.mau) },
                { label: 'Người đăng nhập', points: d.activity.series.map((r) => r.loggedIn) },
              ]} />
            <dl className="facts section">
              <dt>DAU trung bình</dt><dd><Num v={d.activity.avgDau} digits={1} /></dd>
              <dt>MAU ngày {ddmm(d.to)}</dt><dd><Num v={d.activity.mau} /></dd>
              <dt>Độ gắn bó (DAU trung bình / MAU)</dt><dd><Rate v={d.activity.stickiness} /></dd>
              <dt>Người đăng nhập, lượt đăng nhập</dt><dd><Num v={d.activity.loggedInPlayers} /> người, <Num v={d.activity.logins} /> lượt (<Num v={d.activity.loginsPerPlayer} digits={1} /> lượt mỗi người)</dd>
            </dl>
          </Panel>

          <Panel title="Doanh thu theo người chơi">
            <dl className="facts">
              <dt>GGR</dt><dd><Money v={d.revenue.ggr} signed unit /></dd>
              <dt>GGR người mới (đăng ký trong khoảng)</dt><dd><Money v={d.revenue.ggrNew} signed unit /></dd>
              <dt>GGR người cũ</dt><dd><Money v={d.revenue.ggrReturning} signed unit /></dd>
              <dt>Thưởng, hoa hồng đại lý</dt><dd>{d.revenue.bonus === null ? <span className="muted">không tính khi lọc theo game</span> : <><Money v={d.revenue.bonus} unit />, <Money v={d.revenue.commission ?? 0} unit /></>}</dd>
              <dt>NGR (GGR - thưởng - hoa hồng)</dt><dd>{d.revenue.ngr === null ? <span className="muted">không tính khi lọc theo game</span> : <Money v={d.revenue.ngr} signed unit />}</dd>
              <dt>Người cược, người nạp</dt><dd><Num v={d.revenue.activeBettors} />, <Num v={d.revenue.depositors} /></dd>
              <dt>ARPU (GGR / người cược)</dt><dd>{d.revenue.arpu === null ? '-' : <Money v={d.revenue.arpu} unit />}</dd>
              <dt>ARPPU (GGR / người nạp)</dt><dd>{d.revenue.arppu === null ? '-' : <Money v={d.revenue.arppu} unit />}</dd>
              <dt>ARPU, ARPPU theo NGR</dt><dd>{d.revenue.arpuNgr === null ? '-' : <Money v={d.revenue.arpuNgr} unit />}, {d.revenue.arppuNgr === null ? '-' : <Money v={d.revenue.arppuNgr} unit />}</dd>
            </dl>
          </Panel>

          <div className="split">
            <Panel title="Rời bỏ và quay lại">
              <dl className="facts">
                <dt>Người nạp có cược 30 ngày trước đó</dt><dd><Num v={d.churn.priorActive} /></dd>
                <dt>Trong số đó không cược 30 ngày gần nhất (rời bỏ)</dt><dd><Num v={d.churn.churned} /> (<Rate v={d.churn.rate} />)</dd>
                <dt>Quay lại sau hơn 30 ngày nghỉ, trong khoảng</dt><dd><Num v={d.churn.reactivated} /></dd>
              </dl>
              <p className="muted">Tính tại ngày {ddmm(d.churn.asOf)}, chỉ người đã nạp tiền.</p>
            </Panel>
            <Panel title="Hạng VIP (doanh số 30 ngày)">
              <table className="plain">
                <thead><tr><th>Hạng</th><th className="r">Người</th></tr></thead>
                <tbody>{d.vip.counts.map((c) => <tr key={c.name}><td className="is-title">{c.name}</td><td className="r"><Num v={c.players} /></td></tr>)}</tbody>
              </table>
              <p className="muted">So với ngày trước khoảng: lên hạng {d.vip.up}, xuống hạng {d.vip.down}, giữ nguyên {d.vip.same}. Ngưỡng hạng trong Cài đặt.</p>
            </Panel>
          </div>
        </>
      )}
    </>
  );
}

type AcqPeriod = { day: string; partial: boolean; registrations: number; ftd: number; conversion: number | null; byKey: Record<string, { registrations: number; ftd: number }> };
type Acq = { rows: { key: string; registrations: number; ftd: number; conversion: number | null; timeToFtdHours: number | null; ngr: number }[]; total: number;
  grain: Grain; periods: AcqPeriod[]; logins: Record<'os' | 'browser' | 'device', { key: string; players: number }[]> };
/** Groups shown as their own column in the period table; the rest of the range's groups add up in "Khác". */
const ACQ_COLUMNS = 6;

/** Registrations of each day, week or month by group: the range's biggest groups, the others together, and the total. */
function AcqPeriods({ d, byLabel, query }: { d: Acq; byLabel: string; query: Record<string, string> }) {
  const g = d.grain, word = PERIOD_WORD[g];
  const keys = d.rows.slice(0, ACQ_COLUMNS).map((r) => r.key);
  const rest = d.rows.length > ACQ_COLUMNS;
  const pair = (x: { registrations: number; ftd: number } | undefined) =>
    !x || !x.registrations ? <span className="muted">-</span> : <><Num v={x.registrations} /> <span className="muted">({x.ftd})</span></>;
  const others = (p: AcqPeriod) => {
    const o = { registrations: 0, ftd: 0 };
    for (const [k, x] of Object.entries(p.byKey)) if (!keys.includes(k)) { o.registrations += x.registrations; o.ftd += x.ftd; }
    return o;
  };
  return (
    <Panel title={`Đăng ký theo ${word} và ${byLabel.toLowerCase()}`} aside={<ExportButton path="/api/admin/analytics/acquisition" query={{ ...query, section: 'periods' }} label={`Xuất CSV theo ${word}`} />}>
      <LineChart title={`Đăng ký và nạp lần đầu theo ${word}`} format={(v) => String(Math.round(v))}
        labels={d.periods.map((p) => periodTick(p.day, g, p.partial))}
        series={[{ label: 'Đăng ký', points: d.periods.map((p) => p.registrations) }, { label: 'Đã nạp lần đầu', points: d.periods.map((p) => p.ftd) }]} />
      <div className="table-scroll">
        <table className="plain">
          <thead><tr><th>{GRAIN_LABEL[g]}</th><th className="r">Tổng</th><th className="r">Tỷ lệ nạp</th>
            {keys.map((k) => <th key={k} className="r">{k || '(không có)'}</th>)}{rest ? <th className="r">Khác</th> : null}</tr></thead>
          <tbody>{[...d.periods].reverse().map((p) => (
            <tr key={p.day}>
              <td className="num is-title">{periodCell(p.day, g, p.partial)}</td>
              <td className="r">{pair(p)}</td><td className="r"><Rate v={p.conversion} /></td>
              {keys.map((k) => <td key={k} className="r">{pair(p.byKey[k])}</td>)}{rest ? <td className="r">{pair(others(p))}</td> : null}
            </tr>
          ))}</tbody>
        </table>
      </div>
      <p className="muted">Mỗi ô: số người đăng ký trong {word} đó (trong ngoặc: số người trong họ đã nạp lần đầu tính đến nay). Cột là {ACQ_COLUMNS} nhóm có nhiều đăng ký nhất trong khoảng; CSV theo {word} có mọi nhóm.{g !== 'day' ? ' ' + PARTIAL_NOTE : ''}</p>
    </Panel>
  );
}
const BY: [string, string][] = [
  ['agent', 'Đại lý'], ['utm_source', 'utm_source'], ['utm_medium', 'utm_medium'], ['utm_campaign', 'utm_campaign'],
  ['landing', 'Trang vào'], ['referrer', 'Trang giới thiệu'], ['os', 'Hệ điều hành'], ['device', 'Thiết bị'], ['browser', 'Trình duyệt'],
];

function Acquisition() {
  const o = useOptions();
  const f = useReportFilters(30, ['by', 'grain'], { by: 'agent' });
  const [grain, pickGrain] = grainFilter(f);
  const q = useQuery({ queryKey: ['analytics', 'acq', f.query], queryFn: () => api.get<Acq>('/api/admin/analytics/acquisition', f.query) });
  const fields = [{ key: 'by', label: 'Nhóm theo', kind: 'select' as const, options: BY }, ...scopeFields(o.data)];
  const byLabel = BY.find(([k]) => k === (f.values.by || 'agent'))?.[1] ?? '';
  return (
    <>
      <GrainSwitch value={grain} onChange={pickGrain} />
      <FilterBar id="sa" fields={fields} filters={f} exportPath="/api/admin/analytics/acquisition" />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !q.data?.rows.length ? (
        <EmptyState title="Không có đăng ký nào trong khoảng này" body="Chọn khoảng ngày khác hoặc bỏ lọc." />
      ) : (
        <>
          <table className="plain">
            <thead><tr><th>{byLabel}</th><th className="r">Đăng ký</th><th className="r">Nạp lần đầu</th><th className="r">Tỷ lệ</th><th className="r">Đến nạp đầu (trung vị)</th><th className="r">NGR mang lại</th></tr></thead>
            <tbody>{q.data.rows.map((r) => (
              <tr key={r.key}>
                <td className="is-title">{r.key || <span className="muted">(không có)</span>}</td>
                <td className="r"><Num v={r.registrations} /></td><td className="r"><Num v={r.ftd} /></td><td className="r"><Rate v={r.conversion} /></td>
                <td className="r"><Hours v={r.timeToFtdHours} /></td><td className="r"><Money v={r.ngr} signed /></td>
              </tr>
            ))}</tbody>
          </table>
          <AcqPeriods d={q.data} byLabel={byLabel} query={f.query} />
          <div className="split">
            {(['device', 'os', 'browser'] as const).map((k) => (
              <Panel key={k} title={k === 'device' ? 'Thiết bị lúc đăng nhập' : k === 'os' ? 'Hệ điều hành' : 'Trình duyệt'}>
                <table className="plain"><tbody>{q.data!.logins[k].map((r) => <tr key={r.key}><td>{r.key || <span className="muted">(không rõ)</span>}</td><td className="r"><Num v={r.players} /></td></tr>)}</tbody></table>
              </Panel>
            ))}
          </div>
        </>
      )}
      <p className="muted note">Nhóm theo nơi người chơi đến lúc đăng ký. NGR mang lại tính từ ngày đăng ký đến hết khoảng. Thiết bị, hệ điều hành, trình duyệt lấy lần đăng nhập cuối trong khoảng của mỗi người.</p>
    </>
  );
}

type Spend = { month: string; asOf: string; rows: { source: string; spend: number; registrations: number; ftd: number; ngr: number; cpa: number | null; cpftd: number | null; roas: number | null }[] };

function SpendTab() {
  const [month, setMonth] = useState(vnDayKey(new Date()).slice(0, 7));
  const q = useQuery({ queryKey: ['analytics', 'spend', month], queryFn: () => api.get<Spend>('/api/admin/analytics/spend', { month }) });
  return (
    <>
      <div className="toolbar-filters">
        <TextInput id="ss-month" type="month" labelText="Tháng" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <span className="toolbar-filters__grow" />
        <ExportButton path="/api/admin/analytics/spend" query={{ month }} />
      </div>
      {q.isLoading ? <BlockSkeleton lines={6} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !q.data?.rows.length ? (
        <EmptyState title="Chưa có chi phí hay đăng ký nào trong tháng" body="Nhập chi phí ở trang Chi phí marketing." />
      ) : (
        <table className="plain">
          <thead><tr><th>Nguồn</th><th className="r">Chi phí</th><th className="r">Đăng ký</th><th className="r">Nạp lần đầu</th><th className="r">NGR</th><th className="r">CPA</th><th className="r">CPFTD</th><th className="r">ROAS</th></tr></thead>
          <tbody>{q.data.rows.map((r) => (
            <tr key={r.source}>
              <td className="is-title">{r.source || <span className="muted">(không có nguồn)</span>}</td>
              <td className="r"><Money v={r.spend} /></td><td className="r"><Num v={r.registrations} /></td><td className="r"><Num v={r.ftd} /></td>
              <td className="r"><Money v={r.ngr} signed /></td><td className="r">{r.cpa === null ? '-' : <Money v={r.cpa} />}</td>
              <td className="r">{r.cpftd === null ? '-' : <Money v={r.cpftd} />}</td><td className="r"><Rate v={r.roas} /></td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <p className="muted note">CPA = chi phí / đăng ký; CPFTD = chi phí / người nạp lần đầu; ROAS = NGR người đăng ký trong tháng mang lại đến nay / chi phí.</p>
    </>
  );
}

export function StatsPlayersPage() {
  const [tab, setTab] = useState(0);
  return (
    <div className="page">
      <PageHeader title="Người chơi và vòng đời" lead={`Chỉ tính người chơi thật (không tài khoản thử, bot, nhà cái), ngày theo ${timeZoneLabel()}, tiền KRW (₩).`} />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label="Loại báo cáo người chơi">
          <Tab>Vòng đời</Tab>
          <Tab>Nguồn khách</Tab>
          <Tab>Chi phí theo nguồn</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{tab === 0 ? <Lifecycle /> : null}</TabPanel>
          <TabPanel>{tab === 1 ? <Acquisition /> : null}</TabPanel>
          <TabPanel>{tab === 2 ? <SpendTab /> : null}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}
